import { Divider } from '@mui/material';
import { Guid } from 'guid-typescript';
import { useState } from 'react';

import TypographyPriceCap from '@/main/components/common/typography/typographyPriceCap';
import FavoriteButton from '@/main/components/shared/favoriteButton/favoriteButton';
import KOGImage from '@/main/components/shared/kogImage/kogImage';
import PriceDiffPercent from '@/main/components/shared/priceDiffPercent/priceDiffPercent';
import QuoteProcurementPartInfo from '@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo';
import StarRatingPartially from '@/main/components/shared/starRating/starRatingPartially';

import ModalPartDetails from '@/main/screens/order/createorder/steps/stepTwo/modalPartDetails/modalPartDetails';

import * as S from './styles';

import { PartTypeEnum } from '@/main/services/enum/PartTypeEnum';
import {
  getTranslateByKey,
  isNullUndefined,
  isNullUndefinedEmpty
} from '@/main/services/utils';
import { formatCurrency } from '@/main/services/utilsNumber';
import { userIsAdmin } from '@/main/services/utilsUser';

import KogModalSideComponent from '@/main/components/modal/ModalSide';
import { QuoteGetItemsResponse } from '@/main/models/response';

interface IBoxSupplierDisplayProps {
  data: QuoteGetItemsResponse;
  showPrices: boolean;
  isRejected?: boolean;
}

interface IDetailSupplier {
  guid: string | null;
  supplierInventoryGuid: boolean;
}

const INITIAL_DETAIL_SUPPLIER: IDetailSupplier = {
  guid: null,
  supplierInventoryGuid: false
};

function BoxSupplierDisplay({
  data,
  showPrices,
  isRejected = false
}: Readonly<IBoxSupplierDisplayProps>) {
  const [openModalDetails, setOpenModalDetails] = useState(false);
  const [detailSupplier, setDetailSupplier] = useState<IDetailSupplier>(
    INITIAL_DETAIL_SUPPLIER
  );

  const isPaintProject = (idPartType: number) =>
    idPartType === PartTypeEnum.Paint;

  const haveInventory = (guidInventory: string | null | undefined) =>
    !isNullUndefined(guidInventory) && guidInventory !== Guid.EMPTY;

  function handleOpenPartDetail(
    supplierInventoryGuid: string,
    guidQuoteSupplierItem: string
  ) {
    setOpenModalDetails(true);
    const request: IDetailSupplier = {
      guid: haveInventory(supplierInventoryGuid)
        ? supplierInventoryGuid
        : guidQuoteSupplierItem,
      supplierInventoryGuid: !haveInventory(supplierInventoryGuid)
    };
    setDetailSupplier(request);
  }

  function renderShippingCompanies() {
    if (data.shipingIntegrationIsUnavailable) {
      return (
        <div className="d-flex">
          {getTranslateByKey(
            'information.unavailableErrorConnectionToShippingCompany'
          )}
        </div>
      );
    }

    if (data.deliveryBySupplier) {
      const daysDelivery = data.deliveryDaysSupplier || 0;
      const message = `${getTranslateByKey('information.supplierDeliver')} - ${getTranslateByKey('information.aboutWorkDays').replace('{0}', String(daysDelivery))}`;

      return (
        <>
          <h6 style={{ fontWeight: 'bold' }}>{message}</h6>
          <S.TextFreeShipping>
            {getTranslateByKey('information.shippingIncluded')}
          </S.TextFreeShipping>
        </>
      );
    }

    const shipCompany = data.quotationShipingCompany;

    if (
      shipCompany &&
      !shipCompany.errorMessage &&
      shipCompany.quotationShipingCompanyItems &&
      shipCompany.quotationShipingCompanyItems.length > 0
    ) {
      const showShippingCostPrice = userIsAdmin() && !data.deliveryBySupplier;

      const totalShipping = data.deliveryBySupplier
        ? 0
        : !data.shipingIntegrationIsUnavailable
          ? shipCompany.quotationShipingCompanyItems[0].totalAmount
          : 0;

      const messageShipping = showShippingCostPrice
        ? `${getTranslateByKey('information.shippingIncluded')} (${formatCurrency(totalShipping)})`
        : getTranslateByKey('information.shippingIncluded');

      return (
        <>
          {shipCompany.quotationShipingCompanyItems.map(
            (item: any, index: number) => {
              const message = `${item.shippingCompany} - ${item.serviceName} - ${getTranslateByKey('information.aboutWorkDays').replace('{0}', String(item.deliveryDays))}`;
              return (
                <h6 key={index} style={{ fontWeight: 'bold' }}>
                  {message}
                </h6>
              );
            }
          )}
          <S.TextFreeShipping>{messageShipping}</S.TextFreeShipping>
        </>
      );
    }

    return <></>;
  }

  return (
    <S.ContainerBox>
      <S.HeaderBox>
        <S.ContainerTitleBox>
          <S.TitleBox>{data.companyName}</S.TitleBox>
          <FavoriteButton
            isFavorite={data.isFavoriteSupplier}
            notClickable
            onToggleFavorite={() => {}}
          />
        </S.ContainerTitleBox>
        <StarRatingPartially rating={data.score ?? 0} />
      </S.HeaderBox>

      {data.quoteSupplierItems?.map(part => (
        <S.BodyBox key={part.guid}>
          <S.ContainerPart>
            <KOGImage
              width={90}
              height={90}
              src={part.quoteItem.imageUrl}
              style={{
                padding: '0',
                cursor: haveInventory(part.guidSupplierInventory)
                  ? 'pointer'
                  : 'default'
              }}
              onClick={() =>
                handleOpenPartDetail(part.guidSupplierInventory, part.guid)
              }
            />

            <S.ColumnPart>
              <S.PartInfo>
                <QuoteProcurementPartInfo
                  isTooltip
                  data={part.quoteItem.quoteProcurementPartInfo}
                  isEllipsisText={false}
                  renderType="column"
                  showLineSupplierInfo={{ info: '2lines' }}
                  showIconNotes
                />
              </S.PartInfo>
              <S.Quantity>{part.quantity}</S.Quantity>
            </S.ColumnPart>

            {isRejected && (
              <S.Reason>{`${part.rejectReasonName}${part.rejectReasonNote ? ` - ${part.rejectReasonNote}` : ''}`}</S.Reason>
            )}

            <S.ContainerPartPrice>
              {!isRejected && (
                <>
                  <h6>
                    {showPrices && formatCurrency(part.price)}
                    {showPrices &&
                      !isNullUndefined(part.priceDiffPercent) &&
                      !isPaintProject(part.idPartType) && (
                        <PriceDiffPercent diffPercent={part.priceDiffPercent} />
                      )}
                  </h6>
                  {showPrices &&
                    !isPaintProject(part.idPartType) &&
                    (isNullUndefinedEmpty(
                      part.systemReferencePriceFormatted
                    ) ? (
                      <TypographyPriceCap priceCap={part.priceCapFormatted} />
                    ) : (
                      <p style={{ margin: '0' }} translate="no">
                        {getTranslateByKey('information.systemReferencePrice')}:{' '}
                        {part.systemReferencePriceFormatted}
                      </p>
                    ))}
                </>
              )}
            </S.ContainerPartPrice>
          </S.ContainerPart>
        </S.BodyBox>
      ))}

      <Divider />
      {!isRejected && (
        <>
          <S.ContainerDelivery>
            <S.IconShipping />
            <h6 style={{ marginLeft: '0.5rem', fontWeight: 'bold' }}>
              {getTranslateByKey('information.deliveryType')}
            </h6>
          </S.ContainerDelivery>
          <S.ContainerDeliveryPrice>
            <S.ContainerInfoDelivery>
              {renderShippingCompanies()}
            </S.ContainerInfoDelivery>
            {showPrices && (
              <h6 style={{ fontWeight: 'bold' }}>
                {`${getTranslateByKey('information.total')}: ${formatCurrency(data.totalPrice)}`}
              </h6>
            )}
          </S.ContainerDeliveryPrice>
        </>
      )}

      <KogModalSideComponent
        open={openModalDetails}
        closeModal={() => {
          setOpenModalDetails(false);
          setDetailSupplier(INITIAL_DETAIL_SUPPLIER);
        }}>
        {openModalDetails && (
          <ModalPartDetails
            closeModal={() => {
              setOpenModalDetails(false);
              setDetailSupplier(INITIAL_DETAIL_SUPPLIER);
            }}
            guid={detailSupplier.guid}
            quoteSupplierItem={detailSupplier.supplierInventoryGuid}
          />
        )}
      </KogModalSideComponent>
    </S.ContainerBox>
  );
}

export default BoxSupplierDisplay;
